import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Activity,
  Shield,
  GitBranch,
  Route,
  Building2,
  Layers,
  FileCheck,
  Scale,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import SidebarProfileMenu from './SidebarProfileMenu';
import SidebarUtilityStrip from './SidebarUtilityStrip';
import { profileService } from '../../services/users/profileService';
import SidebarDrillTransition from './SidebarDrillTransition';
import { UserRole } from '../../types/database';
import { useSidebarDrill } from '../../contexts/SidebarDrillContext';

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

interface AdminNavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  children?: AdminNavItem[];
}

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/admin', hasSubItems: false },
  {
    id: 'organizations',
    label: 'Organizations',
    icon: Building2,
    path: '/admin/organizations',
  },
  {
    id: 'recruitment-catalog',
    label: 'Recruitment Catalog',
    icon: GraduationCap,
    path: '/admin/recruitment-catalog',
  },
  {
    id: 'compliance-group',
    label: 'Compliance',
    icon: Shield,
    path: '/admin/compliance',
    children: [
      { id: 'compliance', label: 'Compliance', icon: Shield, path: '/admin/compliance' },
      { id: 'activity', label: 'Activity Audit', icon: Activity, path: '/admin/activity' },
      { id: 'consents', label: 'Consent Registry', icon: FileCheck, path: '/admin/consents' },
      {
        id: 'data-rights',
        label: 'Data Subject Requests',
        icon: Scale,
        path: '/admin/data-rights',
      },
    ],
  },
  {
    id: 'system-map',
    label: 'System Map',
    icon: GitBranch,
    path: '/admin/system-map',
  },
  {
    id: 'org-hierarchy',
    label: 'Org Hierarchy',
    icon: Layers,
    path: '/admin/org-hierarchy',
  },
  {
    id: 'user-flows',
    label: 'User Flows',
    icon: Route,
    path: '/admin/user-flows',
  },
] as AdminNavItem[];

const ADMIN_COMPLIANCE_DRILL_PANEL = 'compliance-group';

/** Which admin sidebar drill panel should be open for this route (mirrors coach sidebarDrillPanelForLocation). */
export function adminDrillPanelForPath(pathname: string): string | null {
  if (
    pathname.startsWith('/admin/compliance') ||
    pathname.startsWith('/admin/activity') ||
    pathname.startsWith('/admin/consents') ||
    pathname.startsWith('/admin/data-rights')
  ) {
    return ADMIN_COMPLIANCE_DRILL_PANEL;
  }
  return null;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isCollapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarDrill = useSidebarDrill();
  const { user, signOut } = useAuth();
  const [activeDrillPanel, setActiveDrillPanel] = useState<string | null>(() =>
    adminDrillPanelForPath(location.pathname)
  );
  const [drillDismissed, setDrillDismissed] = useState(false);
  const [fallbackRoleLabel, setFallbackRoleLabel] = useState('Admin');
  const [profileFullName, setProfileFullName] = useState<string | null>(null);

  useEffect(() => {
    const loadRoleFallback = async () => {
      if (!user?.id) {
        setFallbackRoleLabel('Admin');
        setProfileFullName(null);
        return;
      }

      const profile = await profileService.getProfile(user.id);
      setProfileFullName(profile?.full_name?.trim() || null);
      if (profile?.user_role === UserRole.Superadmin) {
        setFallbackRoleLabel('Superadmin');
        return;
      }
      setFallbackRoleLabel('Admin');
    };

    loadRoleFallback();
  }, [user?.id]);

  const pathname = location.pathname;
  const activeId = pathname.startsWith('/admin/organizations')
    ? 'organizations'
    : pathname.startsWith('/admin/recruitment-catalog')
      ? 'recruitment-catalog'
      : pathname.startsWith('/admin/activity')
        ? 'activity'
        : pathname.startsWith('/admin/consents')
          ? 'consents'
          : pathname.startsWith('/admin/data-rights')
            ? 'data-rights'
            : pathname.startsWith('/admin/compliance')
              ? 'compliance'
              : pathname.startsWith('/admin/system-map')
                ? 'system-map'
                : pathname.startsWith('/admin/org-hierarchy')
                  ? 'org-hierarchy'
                  : pathname.startsWith('/admin/user-flows')
                    ? 'user-flows'
                    : pathname.startsWith('/admin')
                      ? 'dashboard'
                      : undefined;
  const complianceChildIds = new Set(['compliance', 'activity', 'consents', 'data-rights']);
  const activeRootId = complianceChildIds.has(activeId || '') ? 'compliance-group' : activeId;
  const navSignature = location.pathname;
  const drillPanelForLocation = useMemo(
    () => adminDrillPanelForPath(location.pathname),
    [location.pathname]
  );
  const prevNavSignatureRef = useRef(navSignature);

  useEffect(() => {
    if (!sidebarDrill) return;
    return sidebarDrill.registerDrillHandler((panelId) => {
      if (!isCollapsed) {
        setDrillDismissed(false);
        setActiveDrillPanel(panelId);
      }
    });
  }, [sidebarDrill, isCollapsed]);

  useEffect(() => {
    setDrillDismissed(false);
  }, [navSignature]);

  useEffect(() => {
    if (isCollapsed) return;

    const prevSignature = prevNavSignatureRef.current;
    const navChanged = navSignature !== prevSignature;
    prevNavSignatureRef.current = navSignature;

    if (navChanged && drillPanelForLocation && !drillDismissed) {
      setActiveDrillPanel(drillPanelForLocation);
      return;
    }

    if (navChanged && !drillPanelForLocation && activeDrillPanel) {
      setActiveDrillPanel(null);
    }
  }, [isCollapsed, navSignature, drillPanelForLocation, drillDismissed, activeDrillPanel]);

  const drillBackClass =
    'relative w-full flex items-center justify-center p-3 rounded-lg font-medium text-gray-800 hover:bg-gray-100 transition-colors';
  const drillItemClass =
    'w-full text-left p-3 rounded-lg hover:bg-gray-100 transition-colors text-gray-700 font-medium';
  const drillItemActiveClass = 'bg-gray-100 text-gray-900 font-medium';

  return (
    <Sidebar
      isCollapsed={isCollapsed}
      onToggle={onToggle}
      navContent={
        <SidebarDrillTransition
          drillKey={!isCollapsed && activeDrillPanel ? activeDrillPanel : null}
          drillMenu={
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => {
                  setDrillDismissed(true);
                  setActiveDrillPanel(null);
                }}
                className={drillBackClass}
              >
                <ChevronLeft className="absolute left-3 h-4 w-4" aria-hidden />
                <span>Compliance</span>
              </button>
              <ul className="space-y-1">
                {navItems
                  .find((item) => item.id === 'compliance-group')
                  ?.children?.map((child) => {
                    const ChildIcon = child.icon;
                    const isChildActive = activeId === child.id;
                    return (
                      <li key={child.id}>
                        <button
                          type="button"
                          onClick={() => navigate(child.path)}
                          className={`${drillItemClass} ${isChildActive ? drillItemActiveClass : ''}`}
                        >
                          <span className="inline-flex items-center">
                            <ChildIcon className="h-4 w-4 flex-shrink-0 mr-2" />
                            {child.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
              </ul>
            </div>
          }
          rootMenu={
            <ul className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const hasChildren = Boolean(item.children?.length);
                const isActive = activeRootId === item.id;

                return (
                  <li key={item.id}>
                    <button
                      onClick={() => {
                        if (!isCollapsed && hasChildren) {
                          setActiveDrillPanel(item.id);
                          navigate(item.path);
                          return;
                        }
                        navigate(item.path);
                      }}
                      className={`w-full flex items-center p-3 rounded-lg transition-colors ${
                        isActive ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      {!isCollapsed && (
                        <>
                          <span className="font-medium ml-3">{item.label}</span>
                          {hasChildren && (
                            <ChevronRight
                              className={`h-4 w-4 ml-auto transition-transform ${
                                activeDrillPanel === item.id ? 'rotate-90' : ''
                              }`}
                            />
                          )}
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          }
        />
      }
      footerContent={
        <div className={`px-3 ${isCollapsed ? 'pt-1.5 pb-0' : 'pt-2 pb-0'}`}>
          <SidebarUtilityStrip
            isCollapsed={isCollapsed}
            persona="admin"
            notificationsPath="/admin/notifications"
            inboxPath="/admin/inbox"
            settingsPath="/admin/settings"
          />
        </div>
      }
      profileContent={
        <SidebarProfileMenu
          isCollapsed={isCollapsed}
          displayName={profileFullName || user?.user_metadata?.full_name || fallbackRoleLabel}
          email={user?.email || 'admin@glyyd.com'}
          profilePath="/admin/settings"
          onSignOut={() => signOut()}
          avatar={
            <div className="h-8 w-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
              {user?.email ? user.email.charAt(0).toUpperCase() : 'A'}
            </div>
          }
        />
      }
    />
  );
};

export default AdminSidebar;
